import { useEffect, useRef } from "react"

import { useTranslation } from "react-i18next"

import { useUISettings } from "@/hooks/use-module-settings"
import { useWhatsNew } from "@/hooks/use-whats-new"
import { useWhatsNewStore } from "@/stores/whats-new-store"

import { WhatsNewDialogShell } from "./whats-new-dialog-shell"

/**
 * The user-facing What's New dialog.
 *
 * Auto-opens once per session when the server's `shouldShow` decision says
 * the resolved version differs from the user's `whats_new_last_seen_version`,
 * and writes that pointer back on acknowledge so we don't nag the same user
 * on every page load.
 *
 * Mounted globally in `protected-route.tsx`. The admin preview path lives in
 * `WhatsNewPreviewDialog` — keep these two concerns separate so the user
 * dialog never accidentally opens on top of an admin preview.
 */
export function WhatsNewDialog({ autoOpen = true }: { autoOpen?: boolean }) {
  const { open, setOpen } = useWhatsNewStore()
  const { updateSettings } = useUISettings()
  const { t, i18n } = useTranslation()

  const { data, isPending } = useWhatsNew(i18n.language)

  // One-shot per mount: don't reopen the dialog after the user dismisses it
  // even if the query refetches and shouldShow stays true.
  const hasOpenedRef = useRef(false)

  useEffect(() => {
    if (!autoOpen || hasOpenedRef.current || !data) {
      return
    }
    if (data.shouldShow) {
      hasOpenedRef.current = true
      setOpen(true)
    }
  }, [autoOpen, data, setOpen])

  function handleAcknowledge() {
    if (data) {
      updateSettings({ whats_new_last_seen_version: data.version })
    }
    setOpen(false)
  }

  return (
    <WhatsNewDialogShell
      // Forward the real query state so a manually-opened dialog (e.g. from
      // a user-menu re-open) doesn't render a blank body before data lands.
      // For the auto-open path this is moot — the open trigger fires after
      // data resolves — but it's the right structural default.
      isLoading={isPending}
      items={data?.items ?? null}
      markdown={data?.markdown ?? null}
      open={autoOpen && open}
      title={t("pages.whats_new.title")}
      onAcknowledge={handleAcknowledge}
      onOpenChange={setOpen}
    />
  )
}
